import React, { useEffect } from 'react';
import { Field as FormikField, FieldProps } from 'formik';
import styled, { css } from 'styled-components';

import Label from 'packages/kakapo-ui-kit/components/forms/controls/Label';
import FieldErrorMessage from 'packages/kakapo-ui-kit/components/forms/errors/FieldErrorMessage';
import Breakpoint from 'packages/kakapo-ui-kit/helpers/Breakpoint';
import { toRgbString } from 'packages/kakapo-ui-kit/helpers/HexColor';
import InputResizable from 'packages/kakapo-ui-kit/components/forms/controls/InputResizable';

export enum FieldType {
	Telephone = 'tel',
	Text = 'text',
	Email = 'email',
	Submit = 'submit',
	Password = 'password',
	Number = 'number',
}

interface IElementProps {
	fullWidth?: boolean;
	small?: boolean;
	className?: string;
	style?: any;
}

interface IProps extends IElementProps {
	name: string;
	label?: string;
	labelColor?: string;
	type: FieldType;
	autoFocus?: boolean;
	resizable?: boolean;
	unspaced?: boolean;
	disabled?: boolean;
	focused?: boolean;
	placeholder?: string;
	hideError?: boolean;
	onChange?: React.ChangeEventHandler<HTMLInputElement>;
	onBlur?: (event?: React.ChangeEvent<HTMLInputElement>) => void;
}

interface IFieldStyleProps {
	fullWidth?: boolean;
	small?: boolean;
	inline?: boolean;
	color?: string;
}

export const style = css<IFieldStyleProps>`
	background: ${(props) => (props.inline ? 'transparent' : `${props.theme.colors.whiteBlue}`)};
	border: ${(props) => (props.inline ? 'none' : `1px solid ${props.theme.colors.lightBlueDark}`)};
	color: ${(props) =>
		props.color ? `${props.theme.colors[props.color]}` : `${props.theme.colors.slateBlue}`};
	display: ${(props) => (props.fullWidth ? 'block' : 'inline-block')};
	width: ${(props) => (props.fullWidth ? '100%' : 'auto')};
	border-radius: ${(props) => (props.inline ? '0' : '4px')};
	padding: ${(props) => (props.small ? '3px 12px' : props.inline ? '0' : '8px 18px')};
	box-sizing: border-box;
	text-align: left;
	font-size: ${(props) => (props.inline ? '14px' : '16px')};
	font-family: inherit;
	line-height: 30px;
	transition: box-shadow 120ms ease-out, background-color 120ms ease-out, color 120ms ease-out;
	resize: none;
	text-decoration: none;
	position: relative;

	&:focus {
		background: ${(props) => (props.inline ? 'transparent' : `${props.theme.colors.white}`)};
		border-color: ${(props) =>
			props.inline ? 'none' : `rgba(${toRgbString(props.theme.colors.primary)}, 0.6)`};
		box-shadow: ${(props) =>
			props.inline ? 'none' : `0 1px 6px 0 rgba(${toRgbString(props.theme.colors.primary)}, 0.6)`};
		color: ${(props) =>
			props.color ? `${props.theme.colors.primary}` : `${props.theme.colors.slateBlue}`};
	}

	&:hover {
		color: ${(props) =>
			props.color ? `${props.theme.colors.primary}` : `${props.theme.colors.slateBlue}`};
	}

	::placeholder {
		color: ${(props) => props.theme.colors.lightBlueDark};
		opacity: 1;
	}

	@media screen and (max-width: ${Breakpoint.Small}) {
		padding: 6px 13px;
		font-size: 16px;
	}

	svg {
		margin-right: 10px;
		transform: translateY(3px);
	}

	svg g g {
		color: currentColor;
		fill: currentColor;
	}
`;

const FieldElement = styled(FormikField)`
	${style}
`;

export const InputElement = styled.input<IElementProps>`
	${style}
`;

export const InputResizableElement = styled(InputResizable)`
	${style}
	padding: 11px 18px;
	line-height: 30px;
`;

interface IFieldContainerProps {
	unspaced?: boolean;
}

export const FieldContainer = styled.div<IFieldContainerProps>`
	margin-bottom: ${(props) => (props.unspaced ? 0 : 20)}px;
`;

interface IFieldResizableElementProps {
	name: string;
	id: string;
	autoFocus?: boolean;
	fullWidth?: boolean;
	disabled?: boolean;
	// TODO: Change to correct onBlur type, (event: React.ChangeEvent<HTMLInputElement>) => void
	onBlur?: any;
	innerRef?: (i: HTMLInputElement) => void;
}

// eslint-disable-next-line react/prefer-stateless-function
class FieldResizableElement extends React.Component<IFieldResizableElementProps> {
	render() {
		const { autoFocus, name, id, fullWidth, disabled, onBlur = () => {}, innerRef } = this.props;

		// TODO: Change to correct type
		const handleInnerRef = (i: any) => {
			if (innerRef && i?.inputRef) {
				innerRef(i.inputRef);
			}
		};

		return (
			<FormikField name={name}>
				{({ field: { value }, form: { setFieldValue, setFieldTouched } }: FieldProps<string>) => (
					<InputResizableElement
						ref={(ref) => handleInnerRef(ref)}
						id={id}
						onBlur={(event) => {
							if (onBlur) {
								onBlur(event);
							}
							setFieldTouched(name);
						}}
						onChange={(changedValue) => {
							setFieldValue(name, changedValue.currentTarget.value);
						}}
						lineHeight={30}
						minRows={1}
						maxRows={15}
						disabled={disabled}
						autoFocus={autoFocus}
						fullWidth={fullWidth}
						value={value}
					/>
				)}
			</FormikField>
		);
	}
}

export default function Field({
	name,
	label,
	labelColor,
	type,
	fullWidth,
	autoFocus,
	resizable,
	unspaced,
	disabled,
	placeholder,
	hideError,
	focused,
	small,
	className,
	style,
	onBlur = () => {},
}: IProps) {
	let input: HTMLInputElement | null = null;

	useEffect(() => {
		if (input && focused) {
			input.focus();
		}
	}, [focused]);

	const contents = (
		<>
			{label && <Label title={label} htmlFor={name} color={labelColor} />}

			{resizable !== true ? (
				<FieldElement
					autoFocus={autoFocus}
					name={name}
					id={name}
					type={type}
					fullWidth={fullWidth}
					disabled={disabled}
					placeholder={placeholder}
					onBlur={onBlur}
					small={small}
					className={className}
					style={style}
					innerRef={(i: HTMLInputElement) => {
						input = i;
					}}
				/>
			) : (
				<FieldResizableElement
					autoFocus={autoFocus}
					name={name}
					id={name}
					fullWidth={fullWidth}
					disabled={disabled}
					onBlur={onBlur}
					innerRef={(i: HTMLInputElement) => {
						input = i;
					}}
				/>
			)}
			{!hideError && <FieldErrorMessage name={name} />}
		</>
	);

	return unspaced ? contents : <FieldContainer unspaced={unspaced}>{contents}</FieldContainer>;
}
